/**
 * Volta — Shared Accessibility Utilities
 * Focus trap, focus save/restore, ARIA live region announcer.
 * Loaded with `defer` in theme.liquid — available before DOMContentLoaded modal code runs.
 */

(function () {
  'use strict';

  const FOCUSABLE_SELECTOR =
    'a[href],button:not([disabled]),input:not([disabled]),' +
    'select:not([disabled]),textarea:not([disabled]),' +
    '[tabindex]:not([tabindex="-1"])';

  // ─── Shared live region ───────────────────────────────────────────────────────

  let _announcer = null;

  function getAnnouncer() {
    if (_announcer && document.body.contains(_announcer)) return _announcer;
    _announcer = document.createElement('div');
    _announcer.id = 'milano-announcer';
    _announcer.setAttribute('aria-live', 'polite');
    _announcer.setAttribute('aria-atomic', 'true');
    _announcer.className = 'sr-only';
    _announcer.style.cssText =
      'position:absolute;width:1px;height:1px;padding:0;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;';
    document.body.appendChild(_announcer);
    return _announcer;
  }

  // ─── Public API ───────────────────────────────────────────────────────────────

  window.MilanoA11y = {

    /**
     * Trap keyboard focus within `container`.
     * @param {HTMLElement} container  The modal/dialog element.
     * @param {Function}    onEscape   Called when Escape is pressed. Typically your closeModal fn.
     * @returns {Function} cleanup — call this on close to remove all listeners.
     */
    trapFocus(container, onEscape) {
      const getFocusable = () =>
        Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
          el => !el.closest('[hidden]') && el.offsetParent !== null
        );

      function handleKeydown(e) {
        if (e.key === 'Escape') {
          e.preventDefault();
          if (typeof onEscape === 'function') onEscape();
          return;
        }
        if (e.key !== 'Tab') return;

        const focusable = getFocusable();
        if (!focusable.length) { e.preventDefault(); return; }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          // Shift+Tab: if focus is on first element, wrap to last
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          // Tab: if focus is on last element, wrap to first
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }

      document.addEventListener('keydown', handleKeydown);

      // Return cleanup function
      return function cleanup() {
        document.removeEventListener('keydown', handleKeydown);
      };
    },

    /**
     * Move focus to the first focusable element inside `container`.
     * Falls back to the container itself if nothing is focusable.
     */
    focusFirst(container) {
      const first = container.querySelector(FOCUSABLE_SELECTOR);
      if (first) {
        first.focus();
      } else {
        container.setAttribute('tabindex', '-1');
        container.focus();
      }
    },

    /** Save the currently focused element. Call before opening a modal. */
    saveFocus() {
      return document.activeElement;
    },

    /** Restore focus to a saved element. Call after closing a modal. */
    restoreFocus(el) {
      if (el && typeof el.focus === 'function') {
        el.focus();
      }
    },

    /**
     * Post a message to the shared ARIA live region.
     * @param {string} message     The text to announce.
     * @param {'polite'|'assertive'} [politeness='polite']
     */
    announce(message, politeness = 'polite') {
      const announcer = getAnnouncer();
      announcer.setAttribute('aria-live', politeness);
      // Clear then set to trigger re-announcement of same text
      announcer.textContent = '';
      requestAnimationFrame(() => {
        announcer.textContent = message;
      });
    }
  };

  // Inject live region as soon as DOM is available
  if (document.body) {
    getAnnouncer();
  } else {
    document.addEventListener('DOMContentLoaded', getAnnouncer);
  }

})();
